import { useData } from "../data/DataContext";

/**
 * Diagnostics page. Shows the signed-in user's session, role resolution,
 * matched-person lookup, and counts loaded by DataContext. Useful for
 * support after cutover when someone reports "I can't see X" - one glance
 * here usually reveals whether they're missing a group, missing a People
 * row, or hitting a different bug.
 *
 * No auth gate - any signed-in user can see their own diagnostic info.
 * (Group IDs aren't sensitive; UPN is already in /.auth/me.)
 */
export function DiagPage() {
  const {
    me,
    people,
    objectives,
    ragScores,
    reportingPeriods,
    categories,
    departments,
  } = useData();

  return (
    <div className="mx-auto grid max-w-3xl gap-4">
      <Card title="Signed-in user">
        <Row label="Display name" value={me.displayName} />
        <Row label="UPN" value={me.username} />
        <Row label="Role" value={<RoleBadge role={me.role} />} />
        <Row
          label="Matched person"
          value={
            me.matchedPerson
              ? `${me.matchedPerson.fullName} (id ${me.matchedPerson.id})`
              : "(none - displayName did not match any People row)"
          }
        />
        <Row
          label="Covers"
          value={
            me.covers.length === 0
              ? "(none)"
              : me.covers.map((p) => p.fullName).join(", ")
          }
        />
        <Row
          label="Group IDs"
          value={me.groupIds.length === 0 ? "(none)" : me.groupIds.join(", ")}
          mono
        />
      </Card>

      <Card title="Data loaded">
        <Row label="People" value={String(people.length)} mono />
        <Row label="Objectives" value={String(objectives.length)} mono />
        <Row label="RAG scores" value={String(ragScores.length)} mono />
        <Row label="Reporting periods" value={String(reportingPeriods.length)} mono />
        <Row label="Categories" value={String(categories.length)} mono />
        <Row label="Departments" value={String(departments.length)} mono />
      </Card>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-rule bg-card p-5 shadow-card">
      <div className="label-mono mb-3">{title}</div>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
        {children}
      </dl>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <>
      <dt className="font-medium text-ink-2">{label}</dt>
      <dd className={mono ? "font-mono text-xs break-all text-ink" : "text-ink"}>
        {value}
      </dd>
    </>
  );
}

function RoleBadge({ role }: { role: string }) {
  const tone =
    role === "Admin"
      ? "bg-brass text-nymr-navy-ink"
      : role === "Exec" || role === "CLT"
        ? "bg-nymr-navy text-white"
        : role === "User"
          ? "bg-paper-sunk text-ink"
          : "bg-rag-unset-bg text-ink-3";
  return (
    <span
      className={`inline-block rounded-sm px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] ${tone}`}
    >
      {role}
    </span>
  );
}
